import { useModule } from "@/hooks/use-module";

import type { LabVendor } from "@/types/lab";
import type { Module } from "@/types/tenant";

/**
 * Which module each lab vendor is sold under — the frontend's copy of the backend's
 * `LAB_VENDOR_MODULE`. Keyed by vendor, so adding one is a missing key the type checker
 * catches rather than a silent default to another vendor's entitlement.
 */
export const LAB_VENDOR_MODULE: Record<LabVendor, Module> = {
  junction: "LABS",
  fullscript: "FULLSCRIPT_LABS",
  access: "ACCESS_LABS",
  uploaded: "UPLOADED_LABS",
  in_house: "IN_HOUSE_LABS",
};

/**
 * Whether each lab vendor is enabled for this tenant.
 *
 * One place, because the answer drives three separate surfaces — whether the Labs tab exists at
 * all, which vendors the New lab order menu offers, and which actions a historical order still
 * allows. Encoding the mapping in each of them lets them disagree about a vendor.
 *
 * Hooks are called unconditionally and in a fixed order, so this must stay a literal per vendor
 * rather than a loop over the map.
 */
export function useLabVendorModules(): Record<LabVendor, boolean> {
  return {
    junction: useModule(LAB_VENDOR_MODULE.junction),
    fullscript: useModule(LAB_VENDOR_MODULE.fullscript),
    access: useModule(LAB_VENDOR_MODULE.access),
    uploaded: useModule(LAB_VENDOR_MODULE.uploaded),
    in_house: useModule(LAB_VENDOR_MODULE.in_house),
  };
}

/** True when the tenant holds any lab vendor — the gate on the Labs tab itself. */
export function useHasAnyLabVendor(): boolean {
  const vendors = useLabVendorModules();
  return Object.values(vendors).some(Boolean);
}
